import { useEffect, useMemo, useRef, useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

import Pane from 'components/Common/Pane'
import config from 'constants/config'

import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { OFFER_TYPE_VILLA_SLUG } from 'constants/offer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getCheapestSelectedPackageOptionWithDefault,
  arePricesLoading,
  getFromDurationLabel,
} from 'selectors/offerPage/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import WalledContent from 'components/Common/WalledContent'
import GenericLoginSignupWall from 'components/Common/WalledContent/GenericLoginSignupWall'
import OfferCalendarDatePickerSelect from '../Filters/CalendarPicker/OfferCalendarDatePickerSelect'
import OfferDurationSelect from '../Filters/Duration/OfferDurationSelect'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import OfferBookButton from '../OfferBookButtons/OfferBookButton'
import Backdrop from 'components/utils/Backdrop'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import VillasPriceDetails from './VillasPriceDetails'
import OfferCapacityWithAgesVerticalSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesVerticalSelect'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { getBestVillasDiscounts } from './utils/calculateDiscount'
import { VILLAS_DISCOUNT_LABEL_COUNT } from 'constants/villas'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import StaticCancellationPoliciesComponent from 'components/Common/CancellationPolicies/Rental/StaticConnectionCancellationPolicies'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import { unique } from 'lib/array/arrayUtils'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import useVillasPrice from './useVillasPrice'
import { generateLoyaltyPointsCalculatorVillasOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'

const SummaryPane = styled(Pane)`
  width: 100%;
`

const TwoColumnSelects = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(8)};

  > * {
    overflow: hidden;
  }
`

const TravelPeriodSection = styled.div`
  display: flex;
  flex-direction: column;
`

const CalendarSubFilters: Array<OfferPageFilterType> = ['capacity', 'duration']

interface Props {
  className?: string
  offer: App.VillaOffer
  rooms: Array<App.Occupants>
  previewMode: boolean
  packageView: App.OfferPackageOptionView
  calendarsLoading?: boolean
  durationLabel: string
  checkIn: string | undefined
  checkOut: string | undefined
  duration: number
  activeFilter?: OfferPageFilterType
}

function VillasSummaryPanel(props: Props) {
  const {
    className,
    offer,
    previewMode,
    packageView,
    rooms,
    duration,
    durationLabel,
    checkIn,
    checkOut,
    activeFilter,
  } = props
  const intl = useIntl()
  const priceTrackedRef = useRef(false)

  const {
    package: pkg,
    hotelPrice,
    hotelMemberPrice,
    propertyFees = 0,
    taxesAndFees,
  } = packageView

  const appDispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const availableForPurchase = useIsAvailableForPurchase()

  const showPrices = previewMode || (packageView?.capacityValid && packageView?.hotelAvailable)
  const showIncludedGuestsExceeded = useMemo(
    () =>
      !packageView?.package.roomRate.extraGuestSurcharges.length &&
      includedGuestsExceededItems(rooms || [], packageView?.package, offer),
    [packageView, rooms, offer],
  )

  const showPriceDetails = taxesAndFees > 0 || propertyFees > 0 || config.agentHub.isEnabled

  const canPurchase = availableForPurchase && packageView.capacityValid && packageView.hotelAvailable
  const canBuyNow = !!checkIn && !!rooms && canPurchase

  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
  const ignoreDynamicCancellationPolicy = pkg?.ignoreDynamicCancellationPolicy ?? false

  const filterContainer = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const parent = calendarRef.current?.closest<HTMLDivElement>('.sticky-container')
    if (activeFilter === 'capacity' || activeFilter === 'duration') {
      if (filterContainer.current && parent) {
        parent.style.top = `${-filterContainer.current.offsetTop + 12}px`
        window.setTimeout(() => filterContainer.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 250)
      }
    } else if (activeFilter === 'checkin') {
      if (calendarRef.current && parent) {
        parent.style.top = `${-calendarRef.current.offsetTop + 12}px`

        window.setTimeout(() => calendarRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 250)
      }
    }
    if (!activeFilter && parent) {
      parent.style.top = ''
    }
  }, [activeFilter])

  const capacities = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate.capacities)
  }, [offer])

  const bestVillasDiscounts = getBestVillasDiscounts(offer?.discounts, VILLAS_DISCOUNT_LABEL_COUNT, duration, checkIn)

  useEffect(() => {
    appDispatch(setCustomerValue(hotelPrice))
  }, [appDispatch, hotelPrice])

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const [price] = useVillasPrice(packageView)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorVillasOptions(offer, {
      price: price.hotel,
      memberPrice: price.member,
      pkg,
      propertyFees: packageView.propertyFees,
      nights: pkg?.duration,
    }),
  ], [pkg, price.hotel, price.member, offer, packageView.propertyFees])

  const handleDurationSelect = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch])

  const bookNowLabel = intl.formatMessage({
    id: 'tFODLqvUp1z75npWQcMz',
    defaultMessage: 'Book now',
    description: 'Button text to book villa offer when dates and guests are selected',
  })
  const checkAvailabilityLabel = intl.formatMessage({
    id: 'kR_np7lc8fnEQMUWefCY',
    defaultMessage: 'Check availability',
    description: 'Button text to check villa availability when dates are not yet selected',
  })

  return (
    <SummaryPane
      border="neutral-five"
      padding={24}
      shadow="bottom-medium"
      className={className}
      data-testid="SummaryPane"
    >
      <Backdrop visible={!!activeFilter} portalless />
      <WalledContent enforced={offer.walledGarden} wall={<GenericLoginSignupWall />}>
        {(availableForPurchase || previewMode) && (
          <VerticalSpacer gap={20}>
            {showPrices && (
              <VerticalSpacer gap={16}>
                <Group direction="vertical" gap={16}>
                  <div>
                    <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                    <VillasPriceDetails priceTrackedRef={priceTrackedRef} />
                    <Group direction="vertical" gap={4}>
                      <PriceRowTaxesAndFees
                        endIcon={showPriceDetails && (
                          <OfferPriceDetailsRow
                            offerPage
                            offer={offer}
                            duration={duration}
                            propertyFees={propertyFees}
                            hotelPrice={hotelPrice}
                            hotelMemberPrice={hotelMemberPrice}
                            showMemberPrice={hotelMemberPrice > 0}
                            taxesAndFees={taxesAndFees}
                            rooms={rooms}
                            extraGuestMessage={
                              showIncludedGuestsExceeded ?
                                pkg.roomPolicyDescription :
                                ''
                            }
                            dueAtPropertyMessage={offer.property.taxesAndFeesContent ?? ''}
                            trigger="info-icon"
                            cta={
                              <TextButton kind="primary" fit="flex">
                                {intl.formatMessage(
                                  {
                                    id: '4vbrs6oZWoKOJ_PTI9XB',
                                    defaultMessage: 'View {travelSynonym} options',
                                    description: 'Button text to view travel options in villa price details',
                                  },
                                  { travelSynonym: config.brandConfig.travelSynonym },
                                )}
                              </TextButton>
                            }
                          />
                        )}
                      />
                      <PriceRowAgentHubCommission
                        offerLocations={offerLocations}
                        offerId={offer.id}
                        productType={offer.productType}
                        vendorName={vendorName}
                        packageId={pkg.id}
                      />
                      {!checkIn && !ignoreDynamicCancellationPolicy && <StaticCancellationPoliciesComponent
                        cancellationPolicies={pkg.roomRate.cancellationPolicies}
                        offerType={offer.type}
                      />}
                      <TAOCancellationPolicies
                        offerId={offer.id}
                        useDynamicCancellationPolicies={useDynamicCancellationPolicies && !ignoreDynamicCancellationPolicy}
                        timezoneOffset={offer.property.timezoneOffset}
                        uniqueKey={pkg.uniqueKey}
                        checkInDate={checkIn}
                        checkOutDate={checkOut}
                        occupants={rooms}
                        cancellationPolicy={pkg.roomRate.cancellationPolicy}
                        // we want to hide "excludes flights" wording for villas since there are no flights sold with villas
                        hideExcludesFlights
                        offerType={offer.type}
                      />
                      {bestVillasDiscounts.length > 0 && (
                      // Wrapped in group to prevent label from extending to edge
                        (<Group direction="horizontal">
                          <ProductTypeLabel productType="limited_time_special" />
                        </Group>)
                      )}
                      {bestVillasDiscounts.length > 0 && bestVillasDiscounts.map((discount) =>
                        <div key={discount.id}>
                          <TravelPeriodSection>
                            <BodyText variant="small" weight="normal" colour="neutral-two">
                              {[
                                intl.formatMessage(
                                  {
                                    id: 'rolKF6wyfp9Bk7Kb5Jb5',
                                    defaultMessage: 'Travel between: {startDate} - {endDate}.',
                                    description: 'Discount travel period start and end dates displayed for villa offer package',
                                  },
                                  {
                                    startDate: moment(discount.schedules[0].travelPeriod.from).format(DMY_CASUAL_SHORT_FORMAT),
                                    endDate: moment(discount.schedules[0].travelPeriod.to).format(DMY_CASUAL_SHORT_FORMAT),
                                  },
                                ),
                                discount.minLos && discount.minLos > 0 && intl.formatMessage(
                                  {
                                    id: '3LoYC6ce6ChZgD7jbPoH',
                                    defaultMessage: 'Minimum stay: {count, plural, one {# night} other {# nights}}',
                                    description: 'Minimum number of nights stay requirement for villa discount eligibility',
                                  },
                                  { count: discount.minLos },
                                ),
                              ].filter(Boolean).join(' ')}
                            </BodyText>
                          </TravelPeriodSection>
                        </div>,
                      )}
                    </Group>
                  </div>
                  <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
                </Group>
              </VerticalSpacer>
            )}
            <Group direction="vertical" gap={8}>
              <VerticalSpacer gap={8}>
                <TwoColumnSelects ref={filterContainer}>
                  <OfferCapacityWithAgesVerticalSelect
                    capacities={capacities}
                    disableMultiRoom
                  />
                  <OfferDurationSelect onDurationSelect={handleDurationSelect} />
                </TwoColumnSelects>
                <div ref={calendarRef}>
                  <OfferCalendarDatePickerSelect
                    subFilters={CalendarSubFilters}
                    placement="bottom-end"
                  />
                </div>
              </VerticalSpacer>
              <OfferBookButton
                type="submit"
                packageView={packageView}
              >
                {canBuyNow ? bookNowLabel : checkAvailabilityLabel}
              </OfferBookButton>
            </Group>
          </VerticalSpacer>
        )}
        {(!availableForPurchase && !previewMode) && (
          <Group fullWidth direction="vertical" gap={20}>
            <BodyText variant="large" weight="bold" data-testid="OfferSoldOut">
              {intl.formatMessage({
                id: 'q7f9G3LvaqW1F5I6n7eJ',
                defaultMessage: 'This villa is unavailable',
                description: 'Message shown when villa offer is sold out or unavailable',
              })}
            </BodyText>
            <TextButton
              kind="primary"
              to={`/${OFFER_TYPE_VILLA_SLUG}`}
              fit="flex"
            >
              {intl.formatMessage({
                id: 'BvLWbP51eYg0KkoUq7ix',
                defaultMessage: 'Browse all villas',
                description: 'Link text to browse all villas when current offer is unavailable',
              })}
            </TextButton>
          </Group>
        )}
      </WalledContent>
    </SummaryPane>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    previewMode: appState.system.previewMode,
    packageView: getCheapestSelectedPackageOptionWithDefault(appState, state),
    rooms: state.rooms,
    offer: state.offer,
    checkIn: state.checkInDate,
    checkOut: state.checkOutDate,
    calendarsLoading: arePricesLoading(appState, state),
    duration: state.duration,
    durationLabel: getFromDurationLabel(state, appState),
    activeFilter: state.activeFilter,
  }
}, VillasSummaryPanel)
